import { useEffect } from 'react';
import { trackTabClick, trackButtonClick } from '../services/analyticsService';

/**
 * Custom hook to set up centralized event tracking via event delegation
 * This hook automatically tracks tabs and buttons with data attributes
 *
 * Usage: Call this hook once in your App.jsx
 */
export const useAnalyticsTracking = () => {
  useEffect(() => {
    /**
     * Get page context from various sources (in priority order)
     * 1. Element's data-page-context attribute
     * 2. Closest parent with data-page-name attribute
     * 3. Extract from URL pathname
     * 4. Fallback to 'unknown'
     */
    const getPageContext = (element) => {
      // Check if element has explicit page context
      const explicitContext = element.getAttribute('data-page-context');
      if (explicitContext) return explicitContext;

      // Check for parent with data-page-name
      const pageNameElement = element.closest('[data-page-name]');
      if (pageNameElement) {
        return pageNameElement.getAttribute('data-page-name');
      }

      // Extract from URL pathname
      const pathname = window.location.pathname;
      const pathParts = pathname.split('/').filter(Boolean);

      // Remove 'exsports' from path if present (context path)
      const relevantPath = pathParts.filter(part => part !== 'exsports');

      return relevantPath[relevantPath.length - 1] || 'home';
    };

    /**
     * Central click handler for all trackable elements
     */
    const handleTracking = (e) => {
      // Track Tab Clicks
      const tabElement = e.target.closest('[data-tab-name]');
      if (tabElement) {
        const tabName = tabElement.getAttribute('data-tab-name');
        const pageContext = getPageContext(tabElement);
        const tabCategory = tabElement.getAttribute('data-tab-category') || '';
        const tabLabel = tabElement.getAttribute('data-tab-label') || tabElement.textContent?.trim() || '';

        trackTabClick(tabName, pageContext, {
          ...(tabCategory && { tab_category: tabCategory }),
          ...(tabLabel && { tab_label: tabLabel })
        });

        return; // Don't track as button if it's a tab
      }

      // Track Button Clicks
      const buttonElement = e.target.closest('[data-track-button]');
      if (buttonElement) {
        const buttonName = buttonElement.getAttribute('data-track-button');
        const pageContext = getPageContext(buttonElement);
        const buttonCategory = buttonElement.getAttribute('data-button-category') || '';
        const buttonLabel = buttonElement.getAttribute('data-button-label') || buttonElement.textContent?.trim() || '';
        const buttonAction = buttonElement.getAttribute('data-button-action') || 'click';

        trackButtonClick(buttonName, pageContext, {
          ...(buttonCategory && { button_category: buttonCategory }),
          ...(buttonLabel && { button_label: buttonLabel }),
          ...(buttonAction && { button_action: buttonAction })
        });
      }
    };

    // Add event listener to document for event delegation
    document.addEventListener('click', handleTracking);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('click', handleTracking);
    };
  }, []);
};

export default useAnalyticsTracking;
